import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '@/store/languageStore';
import { getUserMenu } from '@/services/systems/systemsService';
import type { MenuSystem } from '@/types/systems';
import { collectMenuSearchItems, filterMenuSearchItems, type MenuSearchItem } from '@/utils/menuSearch';
import { tModule } from '@/utils/moduleLabels';
import { getIconColors, getModuleIcon } from '@/utils/moduleIcons';

interface LabeledItem extends MenuSearchItem {
  title: string;
  systemTitle: string;
}

/** Sök bland modulerna i användarens meny och öppna den som väljs. */
export default function ModuleSearch({ iconColor }: { iconColor: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [systems, setSystems] = useState<MenuSystem[] | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);

  useEffect(() => {
    if (!open || systems) return;
    let cancelled = false;
    getUserMenu()
      .then((menu) => {
        if (!cancelled) setSystems(menu.systems || []);
      })
      .catch(() => {
        if (!cancelled) setSystems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, systems]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const place = () => {
      const rect = rootRef.current?.getBoundingClientRect();
      if (!rect) return;
      setMenuPos({ top: rect.bottom + 8, right: Math.max(8, window.innerWidth - rect.right) });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  const results = useMemo(() => {
    const labeled: LabeledItem[] = collectMenuSearchItems(systems || []).map((item) => {
      const systemTitle = tModule(t, item.systemName, 'title') || item.fallbackTitle;
      const title = item.moduleName
        ? tModule(t, item.moduleName, 'title', item.systemName) || item.fallbackTitle
        : systemTitle;
      return { ...item, title, systemTitle };
    });
    return filterMenuSearchItems(labeled, query);
  }, [systems, query, t]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const openItem = (item: LabeledItem) => {
    setOpen(false);
    setQuery('');
    if (item.openInNewWindow) {
      window.open(item.route, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(item.route);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, Math.max(results.length - 1, 0)));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
      return;
    }
    if (event.key === 'Enter' && results[activeIndex]) {
      event.preventDefault();
      openItem(results[activeIndex]);
    }
  };

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        className="h-8 w-8 text-[16px] cursor-pointer rounded-full hover:bg-bgLight transition-colors flex items-center justify-center"
        aria-label={t('moduleSearch.open')}
        aria-expanded={open}
        title={t('moduleSearch.open')}
        onClick={() => setOpen((value) => !value)}
      >
        <span style={{ color: iconColor, display: 'inline-flex' }}>
          <MagnifyingGlassIcon className="w-5 h-5" />
        </span>
      </button>
      {open && menuPos && createPortal(
        <div
          ref={menuRef}
          className="fixed z-[1300] w-80 bg-bgContainer rounded-lg shadow-lg border border-border overflow-hidden"
          style={{ top: menuPos.top, right: menuPos.right }}
        >
          <div className="p-2 border-b border-border">
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={onKeyDown}
              placeholder={t('moduleSearch.placeholder')}
              aria-label={t('moduleSearch.placeholder')}
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bgElevated text-textDefault focus:outline-none focus:ring-2 focus:ring-bgPrimary"
            />
          </div>
          <div className="max-h-80 overflow-y-auto">
            {systems === null ? (
              <p className="px-3 py-3 text-sm text-textTertiary">{t('common.loading')}</p>
            ) : !query.trim() ? (
              <p className="px-3 py-3 text-sm text-textTertiary">{t('moduleSearch.hint')}</p>
            ) : results.length === 0 ? (
              <p className="px-3 py-3 text-sm text-textTertiary">{t('moduleSearch.noResults')}</p>
            ) : (
              <ul>
                {results.map((item, index) => {
                  const colors = getIconColors(item.icon);
                  return (
                    <li key={item.key}>
                      <button
                        type="button"
                        onMouseEnter={() => setActiveIndex(index)}
                        onClick={() => openItem(item)}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-left ${
                          index === activeIndex ? 'bg-bgHover' : 'hover:bg-bgHover'
                        }`}
                      >
                        <span className={`p-1 rounded-md ${colors.bgClass} ${colors.textClass}`}>
                          {getModuleIcon(item.icon, 'w-4 h-4')}
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm text-textDefault truncate">{item.title}</span>
                          {item.moduleName && (
                            <span className="block text-xs text-textTertiary truncate">{item.systemTitle}</span>
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
